'use client';
import {useEffect, useRef, useState} from 'react';
import {advanceLoadingProgress} from './loading-progress.js';
import {hasCompletedLogoIntro, markLogoIntroComplete} from './logo-intro.js';

export default function SplashScreen({ready, failed = false}: {ready: boolean; failed?: boolean}) {
  const [dismissed,setDismissed]=useState(false);
  const [running,setRunning]=useState(false);
  const [progress,setProgress]=useState(0);
  const progressTarget=useRef(0);
  const progressValue=useRef(0);
  const introStarted=useRef(false);
  const introFinished=useRef(false);
  useEffect(() => {
    const update = (event: Event) => { progressTarget.current=Math.max(progressTarget.current, Math.min(100, (event as CustomEvent<number>).detail)); };
    window.addEventListener('codex-loading-progress', update);
    return () => window.removeEventListener('codex-loading-progress', update);
  }, []);
  useEffect(() => {
    let frame=0, previous=performance.now();
    const tick=(now: number)=>{
      progressValue.current=advanceLoadingProgress(progressValue.current, ready ? 100 : progressTarget.current, Math.min(now-previous,100), ready);
      previous=now;
      setProgress(Math.floor(progressValue.current));
      if(progressValue.current<100) frame=requestAnimationFrame(tick);
    };
    frame=requestAnimationFrame(tick);
    return ()=>cancelAnimationFrame(frame);
  },[ready]);
  useEffect(() => {
    const finish=()=>{
      if (!introStarted.current || introFinished.current) return;
      introFinished.current=true;
      markLogoIntroComplete();
      setDismissed(true);
      window.dispatchEvent(new Event('codex-enter-world'));
    };
    window.addEventListener('codex-logo-intro-complete',finish);
    return ()=>window.removeEventListener('codex-logo-intro-complete',finish);
  },[]);
  useEffect(()=>{
    if (!ready || introStarted.current) return;
    if (progress<100 && !failed) return;
    const frame=requestAnimationFrame(()=>{
      // Claim the kickoff in the callback so StrictMode cleanup can cancel and
      // reschedule it without leaving the splash marked as already started.
      if (introStarted.current) return;
      introStarted.current=true;
      window.scrollTo({top:0,left:0,behavior:'instant'});
      if (failed || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        if (!failed) markLogoIntroComplete();
        introFinished.current=true;
        setDismissed(true); window.dispatchEvent(new Event('codex-enter-world')); return;
      }
      setRunning(true);
      window.dispatchEvent(new CustomEvent('codex-start-logo-intro', {detail:{repeatVisit:hasCompletedLogoIntro()}}));
    });
    return ()=>cancelAnimationFrame(frame);
  },[ready,failed,progress]);
  useEffect(() => {
    if (!running || dismissed) return;
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new Event('codex-prepare-world'));
      window.dispatchEvent(new Event('codex-skip-logo-intro'));
      if (!introFinished.current) {
        introFinished.current=true;
        setDismissed(true);
        window.dispatchEvent(new Event('codex-enter-world'));
      }
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [running, dismissed]);
  useEffect(()=>{
    if(dismissed) return;
    const previous=document.body.style.overflow;
    document.body.style.overflow='hidden';
    document.documentElement.classList.add('logo-intro-active');
    return ()=>{
      document.body.style.overflow=previous;
      document.documentElement.classList.remove('logo-intro-active');
    };
  },[dismissed]);
  useEffect(()=>{
    if (!dismissed || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const root=document.documentElement;
    root.classList.add('hero-intro-reveal');
    const timer=window.setTimeout(()=>root.classList.remove('hero-intro-reveal'),1600);
    return ()=>{window.clearTimeout(timer);root.classList.remove('hero-intro-reveal');};
  },[dismissed]);
  if(dismissed) return null;
  return <section className={`brand-splash cinematic-splash${running?' is-running':''}`} data-cinematic data-native-scroll aria-label="CodeX cinematic introduction">
    {!running && <div className="cinematic-loader">
      <progress className="sr-only" value={progress} max={100} aria-label="Loading website" />
      <span className="cinematic-loader-number" aria-hidden="true">{progress}<small>%</small></span>
    </div>}
  </section>;
}
